// home.js — load products.json and render the three homepage product grids.

import { cart, registerQuickPool } from './app.js?v=32';
import { t, LOCALE } from './i18n.js?v=13';
const LP = LOCALE === 'nl' ? '' : '/' + LOCALE;
import { loadProducts, loadAllProducts, fmt, CATEGORY_LABELS, findVariant } from './data.js?v=23';

const ADD_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';

function quickAdd(p) {
  const v = findVariant(p, { size: 'M', material: 'paper' });
  cart.add({
    key:       `${p.id}-${v?.id || 'novariant'}`,
    variantId: v?.id || null,
    title:     p.title,
    image:     p.image,
    price:     v?.price ?? p.price,
    qty:       1,
    size:      v?.sizeCode || 'M',
    material:  v?.materialCode || 'paper',
  });
  if (window.openCart) window.openCart();
}

const HEART_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

// Single-row auto-scrolling marquee with drag + touch + pause-on-hover.
// Container's children should be duplicated for seamless loop.
// Touch-safe: autoplay never writes scrollLeft while the user is (momentum-)
// scrolling — a JS write mid-gesture kills native panning and taps on iOS.
function setupMarquee(grid) {
  if (!grid) return;
  const wrap = grid.closest('.strip-wrap');
  const SPEED = 0.6;         // px per frame (~36 px/sec at 60fps)
  let paused = false;
  let halfWidth = 0;
  let accum = 0;
  let isDragging = false;
  let movedDistance = 0;
  let userActiveUntil = 0;   // timestamp: no autoplay writes before this
  let lastSelfWrite = -1;    // scrollLeft value we wrote ourselves
  let touchHeld = false;

  function recalcHalf() {
    halfWidth = grid.scrollWidth / 2;
  }
  recalcHalf();
  setTimeout(recalcHalf, 200);
  setTimeout(recalcHalf, 800);   // also after images settle
  window.addEventListener('resize', recalcHalf);
  Array.from(grid.querySelectorAll('img')).forEach(img => {
    if (img.complete) return;
    img.addEventListener('load', recalcHalf, { once: true });
  });

  function tick() {
    if (!paused && !touchHeld && Date.now() >= userActiveUntil) {
      if (halfWidth === 0) recalcHalf();
      if (halfWidth > 0) {
        accum += SPEED;
        if (accum >= 1) {
          const inc = Math.floor(accum);
          grid.scrollLeft += inc;
          accum -= inc;
          if (grid.scrollLeft >= halfWidth) grid.scrollLeft -= halfWidth;
          lastSelfWrite = grid.scrollLeft;
        }
      }
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  // Pause on hover
  grid.addEventListener('mouseenter', () => paused = true);
  grid.addEventListener('mouseleave', () => { if (!isDragging) paused = false; });

  // Hide swipe-hint after first interaction
  function markTouched() { if (wrap) wrap.classList.add('is-touched'); }

  // Drag-to-scroll (desktop). IMPORTANT: do NOT preventDefault on mousedown —
  // that would kill the click navigation on the <a> product cards. Only kick in
  // once the pointer actually moves past a small threshold.
  const DRAG_THRESHOLD = 5;
  let startX = 0, startScroll = 0;
  grid.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;             // left button only
    isDragging = true; paused = true;
    movedDistance = 0;
    startX = e.pageX - grid.offsetLeft;
    startScroll = grid.scrollLeft;
    markTouched();
    // no preventDefault here — let the click flow through if no drag happens
  });
  document.addEventListener('mouseup', () => {
    if (!isDragging) return;
    isDragging = false;
    grid.classList.remove('is-dragging');
    setTimeout(() => { if (!grid.matches(':hover')) paused = false; }, 1500);
  });
  document.addEventListener('mousemove', (e) => {
    if (!isDragging) return;
    const x = e.pageX - grid.offsetLeft;
    const dx = x - startX;
    const abs = Math.abs(dx);
    movedDistance = Math.max(movedDistance, abs);
    if (abs < DRAG_THRESHOLD) return;       // tiny jitter — not a drag yet
    e.preventDefault();                      // now we're dragging — block text selection
    grid.classList.add('is-dragging');
    grid.scrollLeft = startScroll - dx;
    if (halfWidth > 0) {
      while (grid.scrollLeft < 0)         grid.scrollLeft += halfWidth;
      while (grid.scrollLeft >= halfWidth) grid.scrollLeft -= halfWidth;
    }
  });

  // Touch: hold autoplay for the whole gesture, then let momentum finish.
  // Every scroll event we didn't cause ourselves extends the idle window, so
  // native swipe + momentum never fight the autoplay writes.
  grid.addEventListener('touchstart', () => {
    touchHeld = true;
    userActiveUntil = Date.now() + 2500;
    markTouched();
  }, { passive: true });
  grid.addEventListener('touchend', () => {
    touchHeld = false;
    userActiveUntil = Date.now() + 2500;
  }, { passive: true });
  grid.addEventListener('touchcancel', () => {
    touchHeld = false;
    userActiveUntil = Date.now() + 2500;
  }, { passive: true });
  grid.addEventListener('scroll', () => {
    markTouched();
    if (Math.abs(grid.scrollLeft - lastSelfWrite) > 3) {
      userActiveUntil = Date.now() + 2500;   // momentum or manual scroll
    }
  }, { passive: true });

  // Prevent click navigation after drag
  grid.addEventListener('click', (e) => {
    if (movedDistance > 6) { e.preventDefault(); e.stopPropagation(); }
    movedDistance = 0;
  }, true);
}

function cardHTML(p, opts = {}) {
  const dark = !!opts.dark;
  const sale = p.onSale;
  const discount = sale ? Math.round(100 - (p.price / p.originalPrice) * 100) : 0;
  const badge = p.isBestseller ? `<span class="p-card__badge badge-bestseller">${t('badge_bestseller')}</span>` :
                p.isNew ? `<span class="p-card__badge badge-new">${t('badge_new')}</span>` :
                sale ? `<span class="p-card__badge badge-sale">-${discount}%</span>` : '';
  const cat = CATEGORY_LABELS[p.category] || (p.category ? p.category.charAt(0).toUpperCase() + p.category.slice(1) : t('cat_poster'));
  // ALWAYS render the original-price slot so card heights are identical with-or-without sale.
  // When no sale, render a non-breaking space placeholder.
  return `
    <div class="p-card-wrap">
      <a class="p-card ${dark ? 'p-card--dark' : ''}" href="${LP}/products/${p.id}">
        <div class="p-card__img">
          <img src="${p.image}" alt="${p.title}" loading="lazy" />
          ${badge}
          <button class="p-card__wish" aria-label="♥ ${p.title}" data-wishlist="${p.id}">${HEART_ICON}</button>
        </div>
        <div class="p-card__body">
          <span class="p-card__eyebrow label">${cat}</span>
          <h3 class="p-card__title">${p.title}</h3>
          <div class="p-card__row">
            <span class="p-card__price-block">
              <span class="p-card__price">vanaf <span class="tabular">${fmt(p.price)}</span></span>
              <span class="p-card__price-orig tabular">${sale ? `<del>${fmt(p.originalPrice)}</del>` : '&nbsp;'}</span>
            </span>
            <span class="p-card__stars stars" aria-label="${Number(p.rating).toFixed(1)} van 5 sterren">
              <span class="rating-stars" style="--pct:${(Number(p.rating)/5*100).toFixed(1)}%"><span class="rating-stars__fill">★★★★★</span></span>
              <span class="tabular">${Number(p.rating).toFixed(1)}</span>
            </span>
          </div>
        </div>
      </a>
      <button class="p-card__quick" aria-label="${t('card_add_aria', { title: p.title })}" data-quick-add="${p.id}">${ADD_ICON}</button>
    </div>
  `;
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-add]');
  if (btn) {
    e.preventDefault();
    const id = btn.dataset.quickAdd;
    const p = window.__products?.find(x => x.id === id);
    if (p && (p.variants || []).length) { quickAdd(p); return; }
    // SSR-kaarten (o.a. tuinposters) dragen hun variant zelf: de index heeft
    // voor deze producten geen variant-data, dus de knop is de bron.
    if (btn.dataset.variantId) {
      cart.add({
        key:       `${id}-${btn.dataset.variantId}`,
        variantId: Number(btn.dataset.variantId),
        title:     btn.dataset.title || (p ? p.title : id),
        image:     btn.dataset.image || (p ? p.image : ''),
        price:     Number(btn.dataset.price) || 0,
        qty:       1,
        size:      btn.dataset.size || '',
        material:  btn.dataset.material || '',
      });
      if (window.openCart) window.openCart();
    } else if (p) { quickAdd(p); }
    return;
  }
  // Wishlist toggle (localStorage only; phase 2 will sync to customer account)
  const w = e.target.closest('[data-wishlist]');
  if (w) {
    e.preventDefault();
    const id = w.dataset.wishlist;
    const list = JSON.parse(localStorage.getItem('25as_wishlist_v1') || '[]');
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
    else list.push(id);
    localStorage.setItem('25as_wishlist_v1', JSON.stringify(list));
    w.classList.toggle('is-active', i < 0);
  }
});

// Hydrate wishlist hearts on load
document.documentElement.addEventListener('partials:ready', () => {
  const list = JSON.parse(localStorage.getItem('25as_wishlist_v1') || '[]');
  list.forEach(id => {
    document.querySelectorAll(`[data-wishlist="${id}"]`).forEach(el => el.classList.add('is-active'));
  });
});

(async () => {
  const products = await loadProducts();
  if (!products.length) return;
  window.__products = products;
  registerQuickPool(products);

  const bestsellers = document.getElementById('grid-bestsellers');
  const popular = document.getElementById('grid-popular');
  const newest  = document.getElementById('grid-new');

  // Bestsellers strip — top 12 by bestseller_rank, single auto-scrolling row.
  if (bestsellers) {
    const picks = products.slice()
      .sort((a, b) => a.bestsellerRank - b.bestsellerRank)
      .slice(0, 12)
      .map(p => ({ ...p, isBestseller: true }));
    // Render the cards twice — the marquee duplicates content for seamless infinite scroll.
    const html = picks.map(p => cardHTML(p, { dark: true })).join('');
    bestsellers.innerHTML = html + html;
    setupMarquee(bestsellers);
  }
  // "Deze week populair" — next-tier bestsellers, white-interval marquee
  if (popular) {
    const picks = products.slice()
      .sort((a, b) => a.bestsellerRank - b.bestsellerRank)
      .slice(8, 20);
    const html = picks.map(p => cardHTML(p)).join('');
    popular.innerHTML = html + html;
    setupMarquee(popular);
  }
  // "Nieuw binnen" — real isNew flagged products, fallback to random sample
  if (newest) {
    // Altijd de laatst geüploade posters: sorteer op publicatiedatum (nieuwste eerst).
    const byDate = (a, b) => (Number(b.publishedAt) || 0) - (Number(a.publishedAt) || 0);
    const flagged = products.filter(p => p.isNew).sort(byDate);
    const base = flagged.length >= 12
      ? flagged.slice(0, 12)
      : products.slice().sort(byDate).slice(0, 12).map(p => ({ ...p, isNew: true }));
    const html = base.map(p => cardHTML(p)).join('');
    newest.innerHTML = html + html;
    setupMarquee(newest);

    // De inline set komt uit de new-arrivals collectie: top-20 op AANMAAKdatum.
    // Producten die later gepubliceerd zijn dan aangemaakt (drafts, migraties)
    // vallen daar buiten. Ververs daarom met de echte nieuwste 12 uit de
    // volledige catalogus zodra die (lazy) binnen is.
    // 25-09-2026: niet meer de volledige catalogus (14 MB) op de homepage laden
    // alleen om deze strip te verversen; dat kostte mobiel seconden aan parse-tijd.
    // De inline set uit new-arrivals is goed genoeg. Oude pad blijft uitgeschakeld.
    if (false) loadAllProducts().then(all => {
      if (!Array.isArray(all) || all.length < 100) return;
      const top = all
        .filter(p => Number(p.publishedAt) > 0 && (p.price ?? 2) > 1 && !/lijst|frame/i.test(p.title || ''))
        .sort(byDate).slice(0, 12)
        .map(p => ({ ...p, isNew: true }));
      if (top.length < 12) return;
      // Guard: alleen verversen als de catalogus-data minstens zo vers is als
      // de inline set — een verouderde all-products.json mag de strip nooit
      // terugzetten naar oudere producten.
      const newestInline = Math.max(0, ...base.map(p => Number(p.publishedAt) || 0));
      if ((Number(top[0].publishedAt) || 0) < newestInline) return;
      if (top.map(p => p.id).join() === base.map(p => p.id).join()) return; // al correct
      const h = top.map(p => cardHTML(p)).join('');
      newest.innerHTML = h + h;
      registerQuickPool(top);
      window.dispatchEvent(new Event('resize'));  // marquee-breedte herberekenen
    }).catch(() => {});
  }
})();

// ============================================================
// Categorie-marquee: op touch-apparaten geen CSS-animatie maar
// dezelfde touch-veilige autoplay-scroll als de productstrips,
// zodat de collectienamen tikbaar en swipebaar zijn.
// ============================================================
(function initTouchMarquee() {
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  document.querySelectorAll('.marquee').forEach(m => {
    m.classList.add('is-scroll');
    setupMarquee(m);
  });
})();

// ============================================================
// Review-rotator — echte Bol-reviews, fade-rotatie elke 5,5s.
// Pauzeert op hover en respecteert prefers-reduced-motion.
// ============================================================
(function initReviewRotator() {
  const wrap = document.getElementById('review-rotator');
  const dataEl = document.getElementById('quotes-data');
  if (!wrap || !dataEl) return;
  let quotes;
  try { quotes = JSON.parse(dataEl.textContent); } catch { return; }
  if (!Array.isArray(quotes) || quotes.length < 2) return;
  // Liquid's t-filter escapet HTML in de vertaalde strings ("z&#39;n"),
  // dus eerst entities terug decoderen voordat we zelf escapen.
  const deent = (s) => { const ta = document.createElement('textarea'); ta.innerHTML = String(s ?? ''); return ta.value; };
  quotes = quotes.map(q => ({ ...q, text: deent(q.text), hl: q.hl ? deent(q.hl) : q.hl, name: deent(q.name) }));

  const textEl = document.getElementById('rq-text');
  const attrEl = document.getElementById('rq-attr');
  const dotsEl = document.getElementById('rq-dots');
  let idx = 0;

  const esc = (s) => s.replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
  function quoteHTML(q) {
    let t = esc(q.text);
    if (q.hl) {
      const h = esc(q.hl);
      t = t.replace(h, `<span class="tone-coral">${h}</span>`);
    }
    return `"${t}"`;
  }
  function renderDots() {
    dotsEl.innerHTML = quotes.map((_, i) =>
      `<button class="rq-dot${i === idx ? ' is-active' : ''}" data-i="${i}" aria-label="Review ${i + 1}"></button>`).join('');
  }
  function show(i) {
    idx = (i + quotes.length) % quotes.length;
    wrap.classList.add('is-fading');
    setTimeout(() => {
      textEl.innerHTML = quoteHTML(quotes[idx]);
      const nm = esc(quotes[idx].name);
      attrEl.innerHTML = `<span class="rq-avatar" aria-hidden="true">${nm.charAt(0).toUpperCase()}</span><span class="rq-name">${nm}</span><span class="rq-verified"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>geverifieerde koper via Bol.com</span>`;
      renderDots();
      wrap.classList.remove('is-fading');
    }, 350);
  }
  renderDots();
  dotsEl.addEventListener('click', (e) => {
    const b = e.target.closest('.rq-dot');
    if (b) { show(Number(b.dataset.i)); restart(); }
  });

  // Vaste hoogte: reserveer ruimte voor de langste quote zodat de pagina
  // niet op en neer springt bij elke rotatie (mobiel én desktop).
  function lockHeight() {
    const probe = textEl.cloneNode(false);
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:auto;min-height:0;width:' + textEl.clientWidth + 'px;';
    textEl.parentNode.appendChild(probe);
    let max = 0;
    quotes.forEach(q => { probe.innerHTML = quoteHTML(q); max = Math.max(max, probe.offsetHeight); });
    probe.remove();
    if (max > 0) textEl.style.minHeight = max + 'px';
  }
  lockHeight();
  window.addEventListener('resize', lockHeight);

  // Swipe op touch: veeg links/rechts door de reviews.
  let swipeX = null;
  wrap.addEventListener('touchstart', (e) => { swipeX = e.changedTouches[0].clientX; }, { passive: true });
  wrap.addEventListener('touchend', (e) => {
    if (swipeX == null) return;
    const dx = e.changedTouches[0].clientX - swipeX;
    swipeX = null;
    if (Math.abs(dx) > 40) { show(idx + (dx < 0 ? 1 : -1)); restart(); }
  }, { passive: true });

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;
  let timer;
  function start() { timer = setInterval(() => show(idx + 1), 3500); }
  function stop() { clearInterval(timer); }
  function restart() { stop(); start(); }
  wrap.addEventListener('mouseenter', stop);
  wrap.addEventListener('mouseleave', start);
  start();
})();
